import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, SwitchCamera, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';

/**
 * Global incoming-call listener.
 *
 * Mounted once at the root of the authenticated app. Polls
 * /api/calls/invitations/incoming every 4 s. When a pending invitation
 * arrives, displays a full-screen modal (above every other screen) with
 * Accept / Decline buttons. Auto-dismisses when invitation expires.
 *
 * Also listens for the `incoming_call` push notification (iOS/Android) so the
 * modal opens instantly when the device is woken up — the poll is a fallback.
 *
 * When the user accepts, requests an Agora token, joins the channel and plays
 * the remote audio. Renders an inline "active call" overlay (mute/hangup).
 */
const IncomingCallProvider = () => {
  const { user } = useAuth();
  const [invite, setInvite] = useState(null); // pending invitation shown as ringing
  const [activeCall, setActiveCall] = useState(null); // { peer, channel_name, app_id, token, uid, started_at }
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [duration, setDuration] = useState(0);
  const [avatarFailed, setAvatarFailed] = useState(false);
  // While the answer call flow is running (token fetch + Agora join), show
  // an immediate visual state so the user knows their tap was registered.
  // Without this, the green button just sat there for 2-3s and users tapped
  // multiple times thinking nothing happened.
  const [accepting, setAccepting] = useState(false);
  const [remoteHasVideo, setRemoteHasVideo] = useState(false);

  const clientRef = useRef(null);
  const localTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const remoteAudioTrackRef = useRef(null);
  // v50.3: keep the remote+local video tracks in refs so a useEffect can
  // (re)attach them to the <div> as soon as `activeCall` mounts. Without this
  // the callee side was calling `.play()` while the container was still null,
  // which is exactly why the caller saw the callee's video but not vice-versa.
  const remoteVideoTrackRef = useRef(null);
  const localVideoTrackPlayRef = useRef(null); // separate reference for playback state
  const remoteVideoElRef = useRef(null);
  const localVideoElRef = useRef(null);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const dismissedIdsRef = useRef(new Set());
  const ringAudioRef = useRef(null);

  // ---------- Ringtone ----------
  const startRingtone = useCallback(() => {
    try {
      if (ringAudioRef.current) return;
      // Lightweight built-in beep loop via WebAudio (no asset dependency)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const gain = ctx.createGain();
      gain.gain.value = 0.04;
      gain.connect(ctx.destination);
      const playBeep = () => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = 880;
        osc.connect(gain);
        osc.start();
        osc.stop(ctx.currentTime + 0.25);
      };
      playBeep();
      const interval = setInterval(playBeep, 1200);
      ringAudioRef.current = { ctx, interval };
    } catch (_) { /* ignore */ }
  }, []);

  const stopRingtone = useCallback(() => {
    try {
      if (!ringAudioRef.current) return;
      clearInterval(ringAudioRef.current.interval);
      ringAudioRef.current.ctx?.close?.();
      ringAudioRef.current = null;
    } catch (_) {}
  }, []);

  // ---------- Polling ----------
  useEffect(() => {
    if (!user?.id) return;

    const poll = async () => {
      // If user has DND on, never accept incoming. The backend already blocks
      // /calls/invite when peer is DND, but double-guard here.
      if (user?.dnd_voice_calls) return;
      // Skip polling while a call is active
      if (activeCall) return;
      try {
        const res = await axios.get('/api/calls/invitations/incoming');
        const inc = res?.data?.invitation;
        if (inc && !dismissedIdsRef.current.has(inc.id)) {
          setInvite(prev => (prev?.id === inc.id ? prev : inc));
        } else if (!inc) {
          setInvite(prev => (prev && Date.parse(prev.expires_at) < Date.now() ? null : prev));
        }
      } catch (_) { /* ignore network errors */ }
    };
    poll();
    // Poll every 2s (was 4s) — keeps perceived ringing latency under 2s.
    pollRef.current = setInterval(poll, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user?.id, user?.dnd_voice_calls, activeCall]);

  // Auto-expire invite locally + ringtone management
  useEffect(() => {
    if (!invite) { stopRingtone(); return; }
    startRingtone();
    const timer = setInterval(() => {
      if (Date.parse(invite.expires_at) < Date.now()) {
        setInvite(null);
        dismissedIdsRef.current.add(invite.id);
        stopRingtone();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [invite, startRingtone, stopRingtone]);

  // ---------- Push notification handler (iOS/Android) ----------
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listeners = [];
    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const onReceived = await PushNotifications.addListener('pushNotificationReceived', (notif) => {
          const data = notif?.data || {};
          if (data.type === 'incoming_call' && data.invitation_id) {
            // Force a refresh of the invitation
            (async () => {
              try {
                const res = await axios.get('/api/calls/invitations/incoming');
                if (res?.data?.invitation) setInvite(res.data.invitation);
              } catch (_) {}
            })();
          }
        });
        const onAction = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const data = action?.notification?.data || {};
          if (data.type === 'incoming_call' && data.invitation_id) {
            (async () => {
              try {
                const res = await axios.get('/api/calls/invitations/incoming');
                if (res?.data?.invitation) setInvite(res.data.invitation);
              } catch (_) {}
            })();
          }
        });
        listeners = [onReceived, onAction];
      } catch (_) { /* push plugin not available */ }
    })();
    return () => listeners.forEach(l => l?.remove?.());
  }, []);

  // ---------- Accept / Decline ----------
  const accept = async () => {
    if (!invite || accepting) return; // Guard against double-tap
    setAccepting(true);
    stopRingtone();
    try {
      const res = await axios.post(`/api/calls/invitations/${invite.id}/answer`);
      const { app_id, channel_name, token, uid, caller, call_type } = res.data;
      const isVideo = call_type === 'video';

      const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;

      client.on('user-published', async (remoteUser, mediaType) => {
        try {
          await client.subscribe(remoteUser, mediaType);
          if (mediaType === 'audio') {
            remoteAudioTrackRef.current = remoteUser.audioTrack;
            remoteUser.audioTrack?.play();
          } else if (mediaType === 'video') {
            // v50.3 fix: retain the track and retry playing it until the DOM
            // node exists (activeCall state change causes a remount).
            remoteVideoTrackRef.current = remoteUser.videoTrack;
            setRemoteHasVideo(true);
            const tryPlay = (attempt = 0) => {
              if (remoteVideoElRef.current && remoteUser.videoTrack) {
                try { remoteUser.videoTrack.play(remoteVideoElRef.current); } catch (_) {}
              } else if (attempt < 20) {
                setTimeout(() => tryPlay(attempt + 1), 150);
              }
            };
            tryPlay();
          }
        } catch (_) {}
      });
      client.on('user-left', () => endActiveCall(true));
      client.on('connection-state-change', (cur) => {
        if (cur === 'DISCONNECTED' || cur === 'FAILED') endActiveCall(true);
      });

      await client.join(app_id, channel_name, token, uid);
      // v50.3 fix — Aggressive echo/noise/gain cancellation. Previously we were
      // only passing the v3 flags (silently ignored on v4+), which produced the
      // "cave" echo the caller heard. We now also re-apply the constraints at
      // the MediaStreamTrack level so mobile WebViews (Android WebView, iOS
      // WKWebView) honour them consistently.
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        AEC: true, ANS: true, AGC: true,
        encoderConfig: 'speech_standard',
      });
      try {
        const ms = audioTrack.getMediaStreamTrack && audioTrack.getMediaStreamTrack();
        if (ms && ms.applyConstraints) {
          await ms.applyConstraints({
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          });
        }
      } catch (_) { /* not fatal */ }
      localTrackRef.current = audioTrack;
      const tracks = [audioTrack];
      if (isVideo) {
        try {
          const videoTrack = await AgoraRTC.createCameraVideoTrack({
            encoderConfig: '480p_2', facingMode: 'user',
          });
          localVideoTrackRef.current = videoTrack;
          localVideoTrackPlayRef.current = videoTrack;
          tracks.push(videoTrack);
          // v50.3 fix: retry playing the local preview until the corner <div>
          // is mounted (setActiveCall below triggers the render).
          const tryPlayLocal = (attempt = 0) => {
            if (localVideoElRef.current) {
              try { videoTrack.play(localVideoElRef.current); } catch (_) {}
            } else if (attempt < 20) {
              setTimeout(() => tryPlayLocal(attempt + 1), 150);
            }
          };
          tryPlayLocal();
        } catch (camErr) {
          toast.error("Caméra indisponible — l'appel continue en audio");
        }
      }
      await client.publish(tracks);

      // v47.4 b95: callee does NOT call /api/calls/start.
      // The 10-min/day quota only applies to the user who INITIATES the call.
      // Incoming calls never consume the callee's daily allotment.

      setActiveCall({
        peer: caller,
        channel_name,
        session_id: null,
        started_at: Date.now(),
        is_video: isVideo,
      });
      setInvite(null);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) toast.error('Appel expiré');
      else if (code === 409) toast.error('Appel déjà terminé');
      else if (code === 429) toast.error('Quota d\'appels atteint');
      else toast.error('Impossible de répondre');
      setInvite(null);
    } finally {
      setAccepting(false);
    }
  };

  const decline = async () => {
    if (!invite) return;
    stopRingtone();
    const id = invite.id;
    dismissedIdsRef.current.add(id);
    setInvite(null);
    try { await axios.post(`/api/calls/invitations/${id}/decline`); } catch (_) {}
  };

  // ---------- Active call cleanup ----------
  const endActiveCall = async (silent = false) => {
    const elapsed = activeCall?.started_at
      ? Math.floor((Date.now() - activeCall.started_at) / 1000)
      : duration;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try {
      if (localTrackRef.current) { localTrackRef.current.stop(); localTrackRef.current.close(); }
    } catch (_) {}
    localTrackRef.current = null;
    try {
      if (localVideoTrackRef.current) { localVideoTrackRef.current.stop(); localVideoTrackRef.current.close(); }
    } catch (_) {}
    localVideoTrackRef.current = null;
    try {
      if (clientRef.current) { await clientRef.current.leave(); clientRef.current.removeAllListeners?.(); }
    } catch (_) {}
    clientRef.current = null;
    if (activeCall?.session_id) {
      try {
        await axios.post('/api/calls/end', {
          session_id: activeCall.session_id,
          duration_seconds: elapsed,
        });
      } catch (_) {}
    }
    if (!silent) toast.info(`Appel terminé · ${formatTime(elapsed)}`);
    setActiveCall(null);
    setMuted(false);
    setCameraOff(false);
    setSpeakerOn(true);
    remoteAudioTrackRef.current = null;
    remoteVideoTrackRef.current = null;
    localVideoTrackPlayRef.current = null;
    setRemoteHasVideo(false);
    setDuration(0);
  };

  const toggleMute = async () => {
    if (!localTrackRef.current) return;
    const next = !muted;
    try { await localTrackRef.current.setEnabled(!next); setMuted(next); } catch (_) {}
  };

  const toggleCamera = async () => {
    if (!localVideoTrackRef.current) return;
    const next = !cameraOff;
    try { await localVideoTrackRef.current.setEnabled(!next); setCameraOff(next); } catch (_) {}
  };

  const switchCamera = async () => {
    if (!localVideoTrackRef.current) return;
    try {
      const cameras = await AgoraRTC.getCameras();
      if (cameras.length < 2) return;
      const cur = localVideoTrackRef.current.getTrackLabel?.() || '';
      const other = cameras.find(c => c.label !== cur) || cameras[0];
      await localVideoTrackRef.current.setDevice(other.deviceId);
    } catch (_) {}
  };

  const toggleSpeaker = () => {
    const next = !speakerOn;
    setSpeakerOn(next);
    if (remoteAudioTrackRef.current) {
      try { remoteAudioTrackRef.current.setVolume(next ? 100 : 30); } catch (_) {}
    }
  };

  // ---------- Render ----------
  // v50.3: once the active call modal is rendered, attach any pending video
  // tracks that arrived before the DOM containers existed.
  useEffect(() => {
    if (!activeCall?.is_video) return;
    if (remoteVideoTrackRef.current && remoteVideoElRef.current) {
      try { remoteVideoTrackRef.current.play(remoteVideoElRef.current); } catch (_) {}
    }
    if (localVideoTrackPlayRef.current && localVideoElRef.current) {
      try { localVideoTrackPlayRef.current.play(localVideoElRef.current); } catch (_) {}
    }
  }, [activeCall, remoteHasVideo]);

  if (!invite && !activeCall) return null;

  // Incoming ringing modal
  if (invite && !activeCall) {
    const remaining = Math.max(0, Math.ceil((Date.parse(invite.expires_at) - Date.now()) / 1000));
    return (
      <div
        className="fixed inset-0 z-[9999] bg-gradient-to-br from-violet-950 via-black to-violet-900 flex flex-col items-center justify-between p-6"
        data-testid="incoming-call-modal"
        style={{
          paddingTop: 'max(2rem, env(safe-area-inset-top))',
          paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
        }}
      >
        <div className="w-full text-center">
          <p className="text-xs text-violet-300 uppercase tracking-widest mb-1" data-testid="incoming-call-label">
            {invite.call_type === 'video' ? 'Appel vidéo entrant' : 'Appel entrant'}
          </p>
          <p className="text-xs text-white/40">Expire dans {remaining}s</p>
        </div>

        <div className="flex flex-col items-center gap-5 text-center">
          <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-violet-500/40 bg-violet-700/30 flex items-center justify-center animate-pulse">
            {invite.caller_avatar && !avatarFailed ? (
              <img
                src={getMediaUrl(invite.caller_avatar)}
                alt=""
                className="w-full h-full object-cover"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span className="text-6xl font-bold text-white/80">
                {(invite.caller_name || '?')[0]?.toUpperCase()}
              </span>
            )}
          </div>
          <h2 className="text-3xl font-semibold text-white" data-testid="incoming-caller-name">
            {invite.caller_name || invite.caller_username}
          </h2>
          <p className="text-sm text-white/50">souhaite vous parler</p>
        </div>

        <div className="flex items-center justify-center gap-10">
          <button
            data-testid="incoming-decline-btn"
            onClick={decline}
            disabled={accepting}
            className="w-20 h-20 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow-2xl shadow-red-900/50 disabled:opacity-50"
            aria-label="decline"
          >
            <PhoneOff className="w-9 h-9 text-white" />
          </button>
          <button
            data-testid="incoming-accept-btn"
            onClick={accept}
            disabled={accepting}
            className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl shadow-green-900/50 transition-colors ${
              accepting
                ? 'bg-green-700 cursor-wait'
                : 'bg-green-600 hover:bg-green-700 animate-pulse'
            }`}
            aria-label="accept"
          >
            {accepting ? (
              <div className="w-9 h-9 border-[3px] border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Phone className="w-9 h-9 text-white" />
            )}
          </button>
        </div>
        {accepting && (
          <p className="text-center text-sm text-green-300 -mt-4 animate-pulse" data-testid="accepting-call-status">
            Connexion en cours…
          </p>
        )}
      </div>
    );
  }

  // Active call (incoming side) — minimal active overlay
  if (activeCall) {
    const isVid = activeCall.is_video;
    return (
      <div
        className={`fixed inset-0 z-[9999] flex flex-col items-center justify-between p-6 ${
          isVid ? 'bg-black' : 'bg-gradient-to-br from-green-950 via-black to-violet-900'
        }`}
        data-testid="incoming-active-call"
        style={{
          paddingTop: 'max(2rem, env(safe-area-inset-top))',
          paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
        }}
      >
        {/* Remote video full screen (video calls) */}
        {isVid && (
          <div
            ref={remoteVideoElRef}
            className="absolute inset-0 bg-black z-0"
            data-testid="incoming-remote-video"
          >
            {!remoteHasVideo && (
              <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                {activeCall.peer.name} n'a pas activé sa caméra
              </div>
            )}
          </div>
        )}
        {/* Local preview corner (video calls) */}
        {isVid && (
          <div
            ref={localVideoElRef}
            className="absolute top-20 right-4 w-24 h-32 sm:w-28 sm:h-36 rounded-xl overflow-hidden border-2 border-white/30 bg-black z-10"
            data-testid="incoming-local-video"
          />
        )}

        <p className="text-xs text-white/60 uppercase tracking-widest relative z-20">
          {isVid ? 'Appel vidéo' : 'En appel'}
        </p>

        {(!isVid || !remoteHasVideo) && (
        <div className="flex flex-col items-center gap-5 text-center relative z-20">
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-green-500/40 bg-violet-700/30 flex items-center justify-center">
            {activeCall.peer.profile_image_url && !avatarFailed ? (
              <img
                src={getMediaUrl(activeCall.peer.profile_image_url)}
                alt=""
                className="w-full h-full object-cover"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span className="text-5xl font-bold text-white/80">
                {(activeCall.peer.name || '?')[0]?.toUpperCase()}
              </span>
            )}
          </div>
          <h2 className="text-2xl font-semibold text-white">{activeCall.peer.name || activeCall.peer.username}</h2>
          <p className="text-4xl font-mono text-green-200" data-testid="incoming-call-timer">{formatTime(duration)}</p>
        </div>
        )}
        {isVid && remoteHasVideo && (
          <div className="absolute top-20 left-4 px-3 py-1.5 bg-black/60 rounded-full backdrop-blur-md z-20">
            <p className="text-sm font-mono text-white">{formatTime(duration)}</p>
          </div>
        )}

        <div className="flex items-center justify-center gap-4 sm:gap-6 relative z-20">
          <button
            onClick={toggleMute}
            data-testid="incoming-mute-btn"
            className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
              muted ? 'bg-red-600 hover:bg-red-700' : 'bg-white/10 hover:bg-white/20'
            }`}
            aria-label={muted ? 'unmute' : 'mute'}
          >
            {muted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
          </button>
          {isVid ? (
            <>
              <button
                onClick={toggleCamera}
                data-testid="incoming-camera-btn"
                className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
                  cameraOff ? 'bg-red-600 hover:bg-red-700' : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="toggle camera"
              >
                {cameraOff ? <VideoOff className="w-6 h-6 text-white" /> : <Video className="w-6 h-6 text-white" />}
              </button>
              <button
                onClick={switchCamera}
                data-testid="incoming-switch-camera-btn"
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-all"
                aria-label="switch camera"
              >
                <SwitchCamera className="w-6 h-6 text-white" />
              </button>
            </>
          ) : (
            <button
              onClick={toggleSpeaker}
              data-testid="incoming-speaker-btn"
              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
                speakerOn ? 'bg-white/10 hover:bg-white/20' : 'bg-white/5 hover:bg-white/10 opacity-60'
              }`}
              aria-label="speaker"
            >
              {speakerOn ? <Volume2 className="w-6 h-6 text-white" /> : <VolumeX className="w-6 h-6 text-white" />}
            </button>
          )}
          <button
            onClick={() => endActiveCall(false)}
            data-testid="incoming-hangup-btn"
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow-2xl shadow-red-900/50"
            aria-label="hangup"
          >
            <PhoneOff className="w-7 h-7 sm:w-9 sm:h-9 text-white" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${String(ss).padStart(2, '0')}`;
};

export default IncomingCallProvider;
